import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthTransitionScreen } from "../../Components/AuthTransitionScreen";
import {
  APPLICATION_NOT_ASSIGNED_PATH,
  completeLoginFromRedirect,
  readLoginReturnTo,
  type LoginRedirectResult,
} from "./authSession.service";
import { clearLoginSelection } from "../../utils/matchesAssignedApplicationOrigin";
import { clearAppLoggedOutFlag } from "../logout";
import {
  isSsoPrefetchRequest,
  notifySsoPrefetchComplete,
  prefetchSisterAppSessions,
} from "./ssoPrefetch";
import type { AuthSessionUser } from "./authSession.types";

type AuthCallbackPageProps = {
  onSessionReady?: (user: AuthSessionUser) => void;
};

/**
 * Handles the one-time post-login redirect from the gateway.
 */
export const AuthCallbackPage: React.FC<AuthCallbackPageProps> = ({
  onSessionReady,
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const result: LoginRedirectResult = await completeLoginFromRedirect();
        if (cancelled) return;

        onSessionReady?.(result.user);
        clearAppLoggedOutFlag();

        if (isSsoPrefetchRequest()) {
          notifySsoPrefetchComplete();
          return;
        }

        if (result.accessDenied) {
          navigate(APPLICATION_NOT_ASSIGNED_PATH, { replace: true });
          return;
        }

        clearLoginSelection();
        void prefetchSisterAppSessions(result.user);
        navigate(readLoginReturnTo(), { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Sign-in failed");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, onSessionReady]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0c1018] px-6 dark:bg-[#0c1018]">
        <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-[#1C202F] p-8 text-center shadow-sm">
          <p className="text-sm font-medium text-red-400">{error}</p>
          <p className="mt-2 text-xs text-slate-400">Redirecting to login…</p>
        </div>
      </div>
    );
  }

  return <AuthTransitionScreen />;
};

export default AuthCallbackPage;
